import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../contexts/ProjectContext';
import { Project, ProjectStatus, ProjectStatusLabels, ProjectStatusColors } from '../types';
import { FolderIcon, UsersIcon, CheckCircleIcon } from '../components/icons/Icons';
import { AppEmptyState, AppSectionHero, AppStatCard, AppToolbar } from '../components/common/AppSurface';
import './ArchivedProjectsPage.css';

const STATUS_FILTERS: { label: string; value: ProjectStatus | 'ALL' }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Planning', value: 'PLANNING' },
  { label: 'On Hold', value: 'ON_HOLD' },
  { label: 'Completed', value: 'COMPLETED' },
];

export const ArchivedProjectsPage: React.FC = () => {
  const { projects, setActiveProjectId, getProjectAnalytics } = useProjects();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ProjectStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');

  const archived = projects.filter(p => p.status !== 'ACTIVE');

  const filtered = archived.filter(p => {
    const matchStatus = filter === 'ALL' || p.status === filter;
    const q = search.toLowerCase();
    const matchSearch = !q
      || p.name.toLowerCase().includes(q)
      || p.code.toLowerCase().includes(q)
      || p.client.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const counts: Record<string, number> = { ALL: archived.length };
  archived.forEach(p => { counts[p.status] = (counts[p.status] || 0) + 1; });

  const openProject = (project: Project) => {
    setActiveProjectId(project.id);
    navigate('/');
  };

  return (
    <div className="arch-page">
      <AppSectionHero
        icon={<FolderIcon size={28} />}
        title="Archived Projects"
        subtitle={`${archived.length} project${archived.length !== 1 ? 's' : ''} in planning, on hold, or completed`}
        aside={
          <>
            <AppStatCard icon={<FolderIcon size={20} />} label="Archived" value={archived.length} caption="Inactive projects" tone="blue" />
            <AppStatCard icon={<CheckCircleIcon size={20} />} label="Completed" value={counts.COMPLETED || 0} caption="Finished projects" tone="mint" />
          </>
        }
      />

      <AppToolbar
        className="arch-toolbar"
        left={
          <div className="arch-filters app-surface-chip-group">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                className={`app-surface-chip-btn arch-filter-btn ${filter === f.value ? 'active' : ''}`}
                onClick={() => setFilter(f.value)}
                style={filter === f.value && f.value !== 'ALL'
                  ? { borderColor: ProjectStatusColors[f.value as ProjectStatus], color: ProjectStatusColors[f.value as ProjectStatus] }
                  : undefined}
              >
                {f.label}
                {counts[f.value] !== undefined && (
                  <span className="app-surface-chip-count arch-filter-count">{counts[f.value]}</span>
                )}
              </button>
            ))}
          </div>
        }
        right={(
          <input
            className="app-surface-input arch-search"
            type="text"
            placeholder="Search by name, code or client..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        )}
      />

      {filtered.length === 0 ? (
        <AppEmptyState
          className="arch-empty"
          icon={<FolderIcon size={28} />}
          title={archived.length === 0 ? 'No archived projects' : 'No matching projects'}
          message={archived.length === 0
            ? 'Projects in planning, on hold, or completed will appear here.'
            : 'Adjust the status filter or search text to see more projects.'}
        />
      ) : (
        <div className="arch-grid">
          {filtered.map(project => (
            <ArchivedProjectCard
              key={project.id}
              project={project}
              analytics={getProjectAnalytics(project)}
              onOpen={() => openProject(project)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface CardProps {
  project: Project;
  analytics: ReturnType<ReturnType<typeof useProjects>['getProjectAnalytics']>;
  onOpen: () => void;
}

function ArchivedProjectCard({ project, analytics, onOpen }: CardProps) {
  const pct = Math.round(analytics.progressPercentage);
  const statusColor = ProjectStatusColors[project.status];

  const fmt = (d: string) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '\u2014';

  return (
    <div className="arch-card">
      <div className="arch-card-header">
        <span className="arch-status-badge" style={{ background: `${statusColor}22`, color: statusColor, borderColor: `${statusColor}55` }}>
          {ProjectStatusLabels[project.status]}
        </span>
        <span className="arch-card-code">{project.code}</span>
      </div>

      <h3 className="arch-card-name" title={project.name}>{project.name}</h3>
      <p className="arch-card-client">{project.client}</p>

      <div className="arch-card-meta">
        <span className="arch-meta-item">
          <UsersIcon size={13} />
          {project.teamMembers.length} member{project.teamMembers.length !== 1 ? 's' : ''}
        </span>
        <span className="arch-meta-item">
          <CheckCircleIcon size={13} />
          {analytics.completedTasks}/{analytics.totalTasks} tasks
        </span>
      </div>

      <div className="arch-progress-bar-wrap">
        <div className="arch-progress-bar">
          <div
            className="arch-progress-fill"
            style={{ width: `${pct}%`, background: statusColor }}
          />
        </div>
        <span className="arch-progress-label">{pct}%</span>
      </div>

      <div className="arch-card-dates">
        <span>{fmt(project.startDate)}</span>
        <span className="arch-dates-sep">{'\u2192'}</span>
        <span>{fmt(project.endDate)}</span>
      </div>

      <button className="arch-open-btn" onClick={onOpen}>
        Open Project {'\u2192'}
      </button>
    </div>
  );
}
